import React, { Component, ErrorInfo, ReactNode } from 'react';
import './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Class component by necessity: componentDidCatch/getDerivedStateFromError
// have no hook equivalent.
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught render error:', error, errorInfo.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary">
        <div className="error-boundary-card">
          <div className="error-boundary-icon" aria-hidden="true">💥</div>
          <h1>Something went wrong</h1>
          <p>
            An unexpected error occurred while rendering this page. Reloading
            usually fixes it — if it keeps happening, please report it.
          </p>
          {this.state.error && (
            <details className="error-boundary-details">
              <summary>Technical details</summary>
              <pre>{this.state.error.message}</pre>
            </details>
          )}
          <div className="error-boundary-actions">
            <button className="error-boundary-btn error-boundary-btn--primary" onClick={this.handleReload}>
              Reload page
            </button>
            <button className="error-boundary-btn" onClick={this.handleGoHome}>
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
